/**
 * Consent-Aware Storage Wrapper
 *
 * Provides a `getConsentedStorage()` function that returns the real
 * `localStorage` when functional-cookie consent has been granted, or
 * falls back to a volatile in-memory store that behaves identically
 * to the Storage API but is discarded when the tab closes.
 *
 * All hooks that persist UI preferences (sidebar state, table column
 * visibility, tour progress, etc.) should use this wrapper instead of
 * accessing `localStorage` directly.
 */

import { readCookieConsent } from "@/lib/utils/cookie-consent";

// ---------------------------------------------------------------------------
// In-memory Storage implementation (Web Storage API compatible)
// ---------------------------------------------------------------------------

class MemoryStorage implements Storage {
  private readonly store = new Map<string, string>();

  get length(): number {
    return this.store.size;
  }

  clear(): void {
    this.store.clear();
  }

  getItem(key: string): string | null {
    return this.store.get(key) ?? null;
  }

  key(index: number): string | null {
    const keys = Array.from(this.store.keys());
    return keys[index] ?? null;
  }

  removeItem(key: string): void {
    this.store.delete(key);
  }

  setItem(key: string, value: string): void {
    this.store.set(key, value);
  }

  // Required by the Storage interface (index signature)
  [name: string]: unknown;
}

// Singleton so all hooks share the same volatile store within a page session
const memoryStorage = new MemoryStorage();

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Returns `localStorage` when functional consent is granted,
 * otherwise returns a volatile in-memory fallback.
 *
 * Safe to call during SSR -- returns the memory fallback when
 * `globalThis.window` is undefined.
 */
export function getConsentedStorage(): Storage {
  if (globalThis.window === undefined) return memoryStorage;

  const consent = readCookieConsent();

  if (!consent?.functional) {
    return memoryStorage;
  }

  return globalThis.localStorage;
}
